import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Package, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { usePricing } from '../context/PricingContext';
import { toast } from 'sonner';
import { resolveImage } from './admin/ImageUpload';
import { computePricing } from '../utils/pricing';

const ProductCard = ({ product, isOnSale }) => {
  const { addItem } = useCart();
  const { user, effectiveUserType } = useAuth();
  const { isFavorite, toggle } = useFavorites();
  const { resolveOverride } = usePricing();
  const navigate = useNavigate();
  const favorited = isFavorite(product.id);
  const userType = effectiveUserType || 'end_user';
  const override = resolveOverride ? resolveOverride(product) : null;
  const pricing = computePricing(product, 1, userType, override);
  // Trade rule: the standard bulk offer is a BUILDER concession only. End
  // users and retailers see it only when the admin has set a CRM override
  // for this specific customer (any discount, fixed price, or custom bulk).
  const showBulkOffer = !!product.bulk_qty && (userType === 'builder' || !!override);
  const bulkPricing = showBulkOffer ? computePricing(product, product.bulk_qty, userType, override) : null;
  const showDiscount = pricing.discount > 0;
  const wasPrice = showDiscount ? product.price : product.wasPrice;
  const discountPct = wasPrice ? Math.round((1 - pricing.unit / wasPrice) * 100) : 0;
  // Auto-detect sale if not explicitly passed (used by generic grids like Home).
  const onSale = isOnSale !== undefined ? isOnSale
    : (product.wasPrice && product.wasPrice > product.price)
      || (product.discount_end_user > 0) || (product.discount_builder > 0) || (product.discount_retailer > 0);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info('Please sign in to add items to your basket');
      navigate('/login?redirect=' + encodeURIComponent(`/product/${product.id}`));
      return;
    }
    // Preserve the variant identity on virtual grid cards so the cart line
    // reflects the exact pack size the customer clicked.
    addItem(product, 1, product.variant_id || null);
    toast.success(`${product.name} added to basket`);
  };

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const res = await toggle(product);
    if (res?.requiresLogin) {
      navigate('/login?redirect=' + encodeURIComponent(`/product/${product.id}`));
    }
  };

  return (
    <Link
      to={product.variant_id
        ? `/products/${product.slug || product.id}?v=${product.variant_id}`
        : `/products/${product.slug || product.id}`}
      data-testid={product.variant_id ? `variant-card-${product.id}-${product.variant_id}` : `product-card-${product.id}`}
      className="group bg-white border border-neutral-200 hover:border-[#F46A25] hover:shadow-lg transition-all rounded-sm flex flex-col overflow-hidden"
    >
      <div className="relative aspect-square bg-neutral-50 overflow-hidden">
        <img
          src={resolveImage(product.image)}
          alt={product.name}
          width="400"
          height="400"
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {product.badge && (
          <div className="absolute top-2 left-2 bg-[#F46A25] text-white text-[11px] font-bold px-2 py-1 rounded-sm">{product.badge}</div>
        )}
        {onSale && (
          <div data-testid={`sale-badge-${product.id}`} className={`absolute ${product.badge ? 'top-9' : 'top-2'} left-2 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-sm shadow-sm flex items-center gap-1`}>
            <span>Sale</span>
          </div>
        )}
        {discountPct > 0 && (
          <div className="absolute top-2 right-2 bg-[#12263A] text-white text-[11px] font-bold px-2 py-1 rounded-sm">-{discountPct}%</div>
        )}
        {showDiscount && (
          <div className="absolute bottom-2 left-2 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm uppercase">Trade price</div>
        )}
        <button
          onClick={handleFavorite}
          aria-label={favorited ? 'Remove from list' : 'Save to list'}
          data-testid={`product-favorite-${product.id}`}
          className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${favorited ? 'bg-[#F46A25] text-white' : 'bg-white/90 text-[#12263A] hover:bg-white hover:text-[#F46A25]'} shadow`}
        >
          <Heart className={`w-4 h-4 ${favorited ? 'fill-white' : ''}`} />
        </button>
      </div>

      <div className="p-3 flex flex-col flex-1">
        <div className="text-[11px] uppercase text-neutral-500 font-semibold mb-1">{product.brand}</div>
        <h3 className="text-sm text-[#12263A] font-medium leading-snug mb-2 line-clamp-2 min-h-[2.5rem]">{product.name}</h3>

        <div className="flex items-center gap-1 mb-2">
          {[1,2,3,4,5].map(i => (
            <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(product.rating) ? 'fill-[#F46A25] text-[#F46A25]' : 'text-neutral-300'}`} />
          ))}
          <span className="text-[11px] text-neutral-600 ml-1">({product.reviews})</span>
        </div>

        <div className="mt-auto">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-lg font-bold text-[#12263A]">£{pricing.unit.toFixed(2)}</span>
            {product.unit && (
              <span className="text-[10px] text-neutral-500 lowercase font-semibold">/ {product.unit}</span>
            )}
            {wasPrice && wasPrice > pricing.unit && (
              <span className="text-xs text-neutral-500 line-through">£{wasPrice.toFixed(2)}</span>
            )}
            <span className="text-[10px] text-neutral-500 uppercase font-semibold">ex VAT</span>
          </div>
          {bulkPricing && bulkPricing.unit < pricing.unit && (
            <div className="mt-1.5 bg-gradient-to-r from-[#FFF6EF] to-orange-50 border border-orange-200 rounded-sm px-2 py-1 flex items-center gap-1.5">
              <Package className="w-3 h-3 text-[#F46A25] flex-shrink-0" />
              <span className="text-[10px] font-bold text-[#F46A25] uppercase">Bulk</span>
              <span className="text-[11px] text-[#12263A] font-semibold">{product.bulk_qty}+ @ £{bulkPricing.unit.toFixed(2)}</span>
            </div>
          )}
          <button
            onClick={handleAdd}
            className="mt-2 w-full bg-[#F46A25] hover:bg-[#e05a15] text-white font-semibold text-sm py-2 rounded-sm flex items-center justify-center gap-2 transition-colors"
          >
            <ShoppingCart className="w-4 h-4" /> Add to basket
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
